// @ts-nocheck
/**
 * Educational Calculator Hub Component
 * Central hub for all educational ROI calculators with A/B testing integration
 * Epic 18: Enhanced marketing ROI calculators with value demonstration
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  ClickableTile,
  Heading,
  Button,
  ContentSwitcher,
  Switch,
  Tag,
  InlineNotification,
  Modal,
  Stack,
  Breadcrumb,
  BreadcrumbItem,
  ProgressIndicator,
  ProgressStep,
  SkeletonText,
} from '@carbon/react';
import {
  Calculator,
  AnalyticsReference,
  Trophy,
  Time,
  Money,
  Education,
  UserMultiple,
  ArrowRight,
  Download,
  Share,
} from '@carbon/icons-react';

import EducationalROICalculator from './EducationalROICalculator';
import { useMarketingAnalytics } from '../../hooks/useMarketingAnalytics';
import styles from './EducationalCalculatorHub.module.scss';

interface EducationalCalculatorHubProps {
  visitorId?: string;
  enableABTesting?: boolean;
  onLeadCapture?: (leadData: any) => void;
  onDemoRequest?: (demoData: any) => void;
}

interface CalculatorType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: 'roi' | 'time-savings' | 'outcomes' | 'cost-benefit';
  calculatorType:
    | 'student-performance'
    | 'teacher-productivity'
    | 'cost-savings'
    | 'learning-outcomes'
    | 'technology-investment'
    | 'parent'
    | 'institution'
    | 'tutor'
    | 'student';
  estimatedTime: string;
  valueProposition: string;
}

const CALCULATOR_TYPES: CalculatorType[] = [
  {
    id: 'roi-calculator',
    name: 'ROI Calculator',
    description: 'Calculate return on investment for educational technology adoption',
    icon: <Calculator size={24} />,
    category: 'roi',
    calculatorType: 'technology-investment',
    estimatedTime: '3-5 minutes',
    valueProposition: 'Demonstrate clear financial benefits of Sigma08 implementation',
  },
  {
    id: 'time-savings-calculator',
    name: 'Time Savings Calculator',
    description: 'Calculate time savings for educators through automation',
    icon: <Time size={24} />,
    category: 'time-savings',
    calculatorType: 'teacher-productivity',
    estimatedTime: '2-3 minutes',
    valueProposition: 'Show how much time teachers can save with Sigma08',
  },
  {
    id: 'student-outcomes-calculator',
    name: 'Student Outcomes Calculator',
    description: 'Calculate improvement in student learning outcomes',
    icon: <Trophy size={24} />,
    category: 'outcomes',
    calculatorType: 'student-performance',
    estimatedTime: '4-6 minutes',
    valueProposition: 'Quantify expected improvements in student performance',
  },
  {
    id: 'cost-benefit-calculator',
    name: 'Cost-Benefit Analysis',
    description: 'Comprehensive cost-benefit analysis for administrators',
    icon: <Money size={24} />,
    category: 'cost-benefit',
    calculatorType: 'cost-savings',
    estimatedTime: '5-7 minutes',
    valueProposition: 'Complete financial analysis for decision makers',
  },
];

/**
 * Educational Calculator Hub Component
 * Provides access to all educational calculators with A/B testing and analytics
 */
export const EducationalCalculatorHub: React.FC<EducationalCalculatorHubProps> = ({
  visitorId,
  enableABTesting = true,
  onLeadCapture,
  onDemoRequest,
}) => {
  const [selectedCalculator, setSelectedCalculator] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedCalculators, setCompletedCalculators] = useState<Set<string>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [abTestVariant, setAbTestVariant] = useState<'control' | 'variant-a' | 'variant-b'>('control');

  const { trackEvent, trackConversion } = useMarketingAnalytics();

  // A/B testing integration
  useEffect(() => {
    if (enableABTesting && visitorId) {
      // Assign visitor to A/B test variant
      const assignTestVariant = async () => {
        try {
          const response = await fetch('/api/ab-tests/assign', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              testId: 'educational-calculator-hub',
              visitorId: visitorId,
            }),
          });

          if (response.ok) {
            const { variant } = await response.json();
            setAbTestVariant(variant);
          }
        } catch (error) {
          console.warn('A/B test assignment failed:', error);
        }
      };

      assignTestVariant();
    }
  }, [enableABTesting, visitorId]);

  const handleCalculatorSelect = (calculatorId: string) => {
    setSelectedCalculator(calculatorId);
    setIsModalOpen(true);

    // Track calculator selection
    trackEvent('calculator_selected', {
      calculator_id: calculatorId,
      ab_test_variant: abTestVariant,
      visitor_id: visitorId,
    });
  };

  const handleCalculatorComplete = (calculatorId: string, results: any) => {
    setCompletedCalculators(prev => new Set([...prev, calculatorId]));

    // Track completion and conversion
    trackEvent('calculator_completed', {
      calculator_id: calculatorId,
      ab_test_variant: abTestVariant,
      visitor_id: visitorId,
      results: results,
    });

    // Track as conversion for A/B testing
    if (enableABTesting && visitorId) {
      trackConversion('educational-calculator-hub', visitorId, {
        calculator_id: calculatorId,
        results: results,
      });
    }
  };

  const handleLeadCapture = (leadData: any) => {
    if (onLeadCapture) {
      onLeadCapture({
        ...leadData,
        source: 'educational-calculator-hub',
        ab_test_variant: abTestVariant,
        completed_calculators: Array.from(completedCalculators),
      });
    }

    // Track lead generation
    trackEvent('lead_captured', {
      source: 'calculator_hub',
      ab_test_variant: abTestVariant,
      visitor_id: visitorId,
      completed_calculators: Array.from(completedCalculators),
    });
  };

  const getVariantLayout = () => {
    switch (abTestVariant) {
      case 'variant-a':
        return 'grid'; // Grid layout
      case 'variant-b':
        return 'carousel'; // Carousel layout
      default:
        return 'list'; // List layout
    }
  };

  const selectedCalculatorData = CALCULATOR_TYPES.find(calc => calc.id === selectedCalculator);

  return (
    <div className={styles.educationalCalculatorHub}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb noTrailingSlash className={styles.calculatorHubBreadcrumb}>
        <BreadcrumbItem href="/marketing">Marketing</BreadcrumbItem>
        <BreadcrumbItem href="/marketing/tools">Tools</BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>Educational Calculators</BreadcrumbItem>
      </Breadcrumb>

      <Grid>
        <Column lg={16} md={8} sm={4}>
          <div className={styles.calculatorHubHeader}>
            <Heading className={styles.calculatorHubTitle}>Educational Value Calculators</Heading>
            <p className={styles.calculatorHubDescription}>
              Demonstrate the measurable impact of Sigma08 on your educational institution. Our interactive calculators
              provide data-driven insights for ROI, time savings, student outcomes, and cost-benefit analysis.
            </p>

            {/* Progress Indicator for A/B variant */}
            {abTestVariant === 'variant-b' && (
              <ProgressIndicator currentIndex={currentStep} className={styles.calculatorHubProgress}>
                <ProgressStep label="Choose Calculator" description="Select the calculator that best fits your needs" />
                <ProgressStep label="Input Data" description="Provide your institutional data" />
                <ProgressStep label="View Results" description="Get personalized insights and recommendations" />
                <ProgressStep label="Request Demo" description="Schedule a personalized demo" />
              </ProgressIndicator>
            )}
          </div>
        </Column>
      </Grid>

      {/* Calculator Selection Grid */}
      <Grid className={styles.calculatorHubGrid}>
        {CALCULATOR_TYPES.map(calculator => {
          const isCompleted = completedCalculators.has(calculator.id);

          return (
            <Column key={calculator.id} lg={4} md={4} sm={4}>
              <ClickableTile
                className={`${styles.calculatorTile} ${isCompleted ? styles.calculatorTileCompleted : ''}`}
                onClick={() => handleCalculatorSelect(calculator.id)}
              >
                <div className={styles.calculatorTileHeader}>
                  <div className={styles.calculatorTileIcon}>{calculator.icon}</div>
                  {isCompleted && (
                    <Tag type="green" size="sm">
                      Completed
                    </Tag>
                  )}
                </div>

                <h3 className={`cds--productive-heading-02 ${styles.calculatorTileTitle}`}>{calculator.name}</h3>

                <p className={styles.calculatorTileDescription}>{calculator.description}</p>

                <div className={styles.calculatorTileMeta}>
                  <Tag type="outline" size="sm">
                    {calculator.estimatedTime}
                  </Tag>
                </div>

                <div className={styles.calculatorTileValue}>
                  <strong>Value:</strong> {calculator.valueProposition}
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--cds-spacing-02)',
                    marginTop: 'var(--cds-spacing-03)',
                    color: 'var(--cds-link-primary)',
                    fontSize: 'var(--cds-helper-text-01-font-size)',
                    fontWeight: 500,
                  }}
                >
                  {isCompleted ? 'View Results' : 'Start Calculator'}
                  <ArrowRight size={16} />
                </div>
              </ClickableTile>
            </Column>
          );
        })}
      </Grid>

      {/* Completion Summary */}
      {completedCalculators.size > 0 && (
        <Grid className={styles.calculatorHubSummary}>
          <Column lg={16} md={8} sm={4}>
            <InlineNotification
              kind="success"
              title="Calculators Completed"
              subtitle={`You've completed ${completedCalculators.size} out of ${CALCULATOR_TYPES.length} calculators. Great progress!`}
              hideCloseButton
            />

            <div className={styles.calculatorHubActions}>
              <Button
                kind="primary"
                renderIcon={Download}
                onClick={() => {
                  trackEvent('results_exported', {
                    completed_calculators: Array.from(completedCalculators),
                    ab_test_variant: abTestVariant,
                  });
                }}
              >
                Export All Results
              </Button>

              <Button
                kind="secondary"
                renderIcon={Share}
                onClick={() => {
                  trackEvent('results_shared', {
                    completed_calculators: Array.from(completedCalculators),
                    ab_test_variant: abTestVariant,
                  });
                }}
              >
                Share Results
              </Button>

              <Button
                kind="tertiary"
                renderIcon={Education}
                onClick={() => {
                  if (onDemoRequest) {
                    onDemoRequest({
                      source: 'calculator_hub',
                      completed_calculators: Array.from(completedCalculators),
                      ab_test_variant: abTestVariant,
                    });
                  }
                }}
              >
                Request Demo
              </Button>
            </div>
          </Column>
        </Grid>
      )}

      {/* Calculator Modal */}
      <Modal
        open={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        modalHeading={selectedCalculatorData?.name || 'Educational Calculator'}
        modalLabel="Sigma08 Value Calculator"
        primaryButtonText="Close"
        size="lg"
        className={styles.calculatorHubModal}
      >
        {selectedCalculatorData && (
          <EducationalROICalculator
            calculatorType={selectedCalculatorData.calculatorType}
            onLeadCapture={handleLeadCapture}
            onDemoRequest={onDemoRequest}
            enableABTesting={enableABTesting}
            testId="educational-calculator-hub"
            visitorId={visitorId}
            onComplete={results => handleCalculatorComplete(selectedCalculator!, results)}
          />
        )}
      </Modal>
    </div>
  );
};

export default EducationalCalculatorHub;
